import {acceptHMRUpdate, defineStore} from "pinia";
import {computed, ref} from "vue";
import {
    MedicationCheckupBlockDeclaration,
    MedicationCheckupCycleState,
    MedicationCheckupDefinitionKey,
    MedicationCheckupRunReport,
    MedicationCheckupStep,
    MedicationCheckupStepDeclaration
} from "@/medication-checkup/interfaces";
import {medicationCheckupApi} from "@/container";
import ConsumerKeyService from "@/consumer/services/ConsumerKeyService";
import {useToastStore} from "@/stores/toast.store";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";

/**
 * Store of the BPM Copilote: the state of the cycle being worked on.
 *
 * It keeps what the HDS serves and derives the rest, so nothing is stored
 * twice and the thread is never held as a list of messages. The HDS composes
 * the views of one step per read, so the store also knows which step the
 * cycle in hand was composed for: that step is the one the thread shows.
 */
export const useMedicationCheckupStore = defineStore('medication-checkup', () => {
    const cycle = ref<MedicationCheckupCycleState | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    /**
     * The patient the cycle is the bilan of, as the HDS keys them.
     *
     * Resolved once from the consumer key of the route and kept: the course
     * itself never needs it again, but the column naming the patient does, and
     * re-deriving it there would duplicate the conversion and its beneficiary
     * case.
     */
    const customerId = ref<number | null>(null);

    /**
     * The step the thread shows.
     *
     * By construction the step whose views the cycle in hand carries: every
     * state received names the step it was composed for, and both are
     * written together, so they can never drift apart.
     */
    const displayedStepKey = ref<MedicationCheckupDefinitionKey | null>(null);

    /**
     * Whether another step is being opened: the views in hand are being
     * swapped while the cycle itself stays on screen.
     */
    const stepLoading = ref(false);

    /**
     * Whether the cycle in hand is being deleted.
     */
    const deleting = ref(false);

    const byPosition = (first: {position: number}, second: {position: number}): number => {
        return first.position - second.position;
    };

    const steps = computed<MedicationCheckupStep[]>(() => {
        return [...(cycle.value?.steps ?? [])].sort(byPosition);
    });

    /**
     * The step being worked on: the first one that is not completed.
     *
     * Derived from the statuses the HDS serves, never recomputed from the
     * blocks — the server owns that derivation.
     */
    const currentStep = computed<MedicationCheckupStep | null>(() => {
        return steps.value.find(step => step.status !== 'completed') ?? null;
    });

    /**
     * The step the thread shows: the one opened, else the one being worked on,
     * else — the course over — the last one, so its record stays in view.
     */
    const displayedStep = computed<MedicationCheckupStep | null>(() => {
        return steps.value.find(step => step.definition_key === displayedStepKey.value)
            ?? currentStep.value
            ?? steps.value.at(-1)
            ?? null;
    });

    /**
     * The blocks of the displayed step, in course order — what the thread and
     * the checklist render from.
     */
    const displayedBlocks = computed(() => {
        return [...(displayedStep.value?.blocks ?? [])].sort(byPosition);
    });

    /**
     * The step after the displayed one, when the course declares one.
     */
    const nextStep = computed<MedicationCheckupStep | null>(() => {
        const index = steps.value.findIndex(step => step === displayedStep.value);

        return index === -1 ? null : steps.value[index + 1] ?? null;
    });

    /**
     * Whether a step can be opened in the thread: one already played, or the
     * one being worked on. A step not reached yet has nothing to show.
     *
     * The one being worked on may still be pending — no block completed yet,
     * as right after the previous step closed — so it is named, not read
     * from its status.
     */
    const canOpenStep = (definitionKey: MedicationCheckupDefinitionKey): boolean => {
        const step = steps.value.find(candidate => candidate.definition_key === definitionKey);

        return step !== undefined && (step.status === 'completed' || step === currentStep.value);
    };

    const stepOfBlock = (blockId: number): MedicationCheckupStep | null => {
        return steps.value.find(step => step.blocks.some(block => block.id === blockId)) ?? null;
    };

    /**
     * The steps as the pinned definition declares them.
     *
     * Labels and order come from the definition, never from front constants:
     * a regulatory change is a new version of the definition, not a release
     * of Connect.
     */
    const declaredSteps = computed<MedicationCheckupStepDeclaration[]>(() => {
        return cycle.value?.definition?.body?.steps ?? [];
    });

    const declaredStep = (definitionKey: string): MedicationCheckupStepDeclaration | null => {
        return declaredSteps.value.find(step => step.key === definitionKey) ?? null;
    };

    const declaredBlock = (definitionKey: string): MedicationCheckupBlockDeclaration | null => {
        for (const step of declaredSteps.value) {
            const block = step.blocks.find(candidate => candidate.key === definitionKey);

            if (block !== undefined) {
                return block;
            }
        }

        return null;
    };

    /**
     * Take a served state in hand, with the step it was composed for.
     *
     * The single way the cycle is written: a state named for no step was
     * composed for the one the course stands at — the HDS default — which
     * falls back to the last step once the course is over, as the HDS does.
     */
    const receive = (
        state: MedicationCheckupCycleState,
        stepKey: MedicationCheckupDefinitionKey | null
    ): void => {
        cycle.value = state;
        displayedStepKey.value = stepKey
            ?? (currentStep.value ?? steps.value.at(-1))?.definition_key
            ?? null;
    };

    /**
     * Enter the medication review of a consumer, on the cycle under way.
     *
     * Entering rather than reading: the HDS action is idempotent and opens
     * what is missing, so a bookmark or a refresh walks into the course
     * instead of stopping on "no review yet", in one round trip.
     *
     * The route carries a consumer key while the HDS keys by customer; a
     * beneficiary key resolves to nothing, the BPM being for the
     * policyholder only.
     */
    const load = async (consumerKey: string): Promise<void> => {
        const id = ConsumerKeyService.customerIdFromKey(
            ConsumerKeyService.defaultKey(consumerKey)
        );

        if (id === null) {
            reset();
            error.value = "Le bilan partagé de médication est seulement disponible pour les assurés.";

            return;
        }

        reset();
        customerId.value = id;
        loading.value = true;

        try {
            receive(await medicationCheckupApi().enter(id), null);
        } catch (exception) {
            error.value = enteringError(exception);
        } finally {
            loading.value = false;
        }
    };

    /**
     * The HTTP status a failed call answered with, when it answered at all: a
     * breakdown of the network carries none.
     */
    const statusOf = (exception: unknown): number | undefined => {
        return (exception as {response?: {status?: number}})?.response?.status;
    };

    /**
     * What to tell the pharmacist when entering the course was refused.
     *
     * A 409 is not a breakdown but a missing definition or a closed year with
     * no successor — states the counter can do nothing about, so they are
     * named rather than hidden behind the generic loading error.
     */
    const enteringError = (exception: unknown): string => {
        if (statusOf(exception) === 409) {
            return "Le bilan partagé de médication ne peut pas être ouvert pour ce patient "
                + "pour le moment.";
        }

        return "Erreur lors du chargement du bilan partagé de médication.";
    };

    /**
     * Submit a block, taking the course to what follows it. The answered
     * state replaces the one in hand: the whole cycle is the unit that moves.
     *
     * The answer is composed for the step of the submitted block, and the
     * thread stays on it — closing a step leaves its record on screen rather
     * than jumping to the next one, which is entered by a move of its own.
     *
     * Answers whether the submission went through. A 409 — another session
     * moved the course on — is recovered from by re-reading rather than
     * thrown, so `false` means the served state moved on its own and the
     * payload was never applied.
     *
     * Any other failure is told before it is thrown: a refusal in the words
     * the handler gave it, the handlers wording their refusals in French, a
     * breakdown generically, and an authorization error not at all — the
     * response interceptor already told it.
     */
    const submitBlock = async (
        blockId: number,
        payload: Record<string, unknown> = {}
    ): Promise<boolean> => {
        if (cycle.value === null) {
            return false;
        }

        const stepKey = stepOfBlock(blockId)?.definition_key ?? displayedStepKey.value;

        try {
            receive(await medicationCheckupApi().submitBlock(blockId, payload), stepKey);

            return true;
        } catch (exception) {
            if (statusOf(exception) === 409) {
                useToastStore().showError({
                    content: "Le bilan a avancé ailleurs, il vient d'être actualisé."
                });

                await refreshCycle();

                return false;
            }

            if (!isAuthorizationError(exception)) {
                useToastStore().showError({content: apiErrorMessage(exception, "L'étape n'a pas pu être validée.")});
            }

            throw exception;
        }
    };

    /**
     * Run a narrated work of a block, with progress reporting.
     *
     * The stream is progress only; the cycle is re-read once it closes, and
     * that read is what the scenes render from. A failed stream is rethrown
     * without a toast — the scene falls back to a plain submission, and that
     * path reports the error.
     */
    const streamBlock = async (
        stream: (signal?: AbortSignal) => Promise<unknown>,
        signal?: AbortSignal
    ): Promise<void> => {
        if (cycle.value === null) {
            return;
        }

        await stream(signal);

        await refreshCycle();
    };

    /**
     * Scan the record of a block: the treatments it carries, read out one by
     * one.
     */
    const scanBlock = (
        blockId: number,
        onReport: (report: MedicationCheckupRunReport) => void,
        signal?: AbortSignal
    ): Promise<void> => {
        return streamBlock(
            abort => medicationCheckupApi().scanBlock(blockId, onReport, abort),
            signal
        );
    };

    /**
     * Run the analysis of a block: its treatments passed over the sources of
     * vigilance, source by source.
     */
    const analyzeBlock = (
        blockId: number,
        onReport: (report: MedicationCheckupRunReport) => void,
        signal?: AbortSignal
    ): Promise<void> => {
        return streamBlock(
            abort => medicationCheckupApi().analyzeBlock(blockId, onReport, abort),
            signal
        );
    };

    /**
     * Ask the AI draft of a block, with what the AI does reported as it does
     * it.
     */
    const draftBlock = (
        blockId: number,
        onReport: (report: MedicationCheckupRunReport) => void,
        signal?: AbortSignal
    ): Promise<void> => {
        return streamBlock(
            abort => medicationCheckupApi().draftBlock(blockId, onReport, abort),
            signal
        );
    };

    /**
     * Reload the state of the current cycle, on the step the thread shows — the
     * scenes render from it, so refreshing is what moves the course on
     * screen, and nothing jumps.
     */
    const refreshCycle = async (): Promise<void> => {
        if (cycle.value === null) {
            return;
        }

        const cycleId = cycle.value.id;
        const stepKey = displayedStepKey.value;

        try {
            receive(await medicationCheckupApi().readCycle(cycleId, stepKey), stepKey);
        } catch {
            error.value = "Erreur lors du rafraîchissement du bilan.";
        }
    };

    /**
     * Open a step in the thread: read the cycle composed for it.
     *
     * Only a step already played or being worked on opens; a failed read
     * leaves the thread where it was, and says so.
     */
    const openStep = async (definitionKey: MedicationCheckupDefinitionKey): Promise<void> => {
        if (cycle.value === null || definitionKey === displayedStepKey.value || !canOpenStep(definitionKey)) {
            return;
        }

        const cycleId = cycle.value.id;
        stepLoading.value = true;

        try {
            receive(await medicationCheckupApi().readCycle(cycleId, definitionKey), definitionKey);
        } catch {
            useToastStore().showError({content: "L'étape n'a pas pu être ouverte."});
        } finally {
            stepLoading.value = false;
        }
    };

    /**
     * Delete the medication review of the patient, whole.
     *
     * Not the cycle in hand but the row holding it: every cycle goes with it,
     * the ones already closed included. The state is dropped on success
     * rather than re-read — there is nothing left to read — and entering
     * again is what opens a first cycle on a new review. Leaving the course
     * is the caller's move: it alone knows where it was entered from.
     *
     * Answers whether the deletion went through. A refusal is named here and
     * the cycle re-read, so the course shows what it stands at rather than a
     * state the HDS has moved past.
     */
    const deleteCheckup = async (): Promise<boolean> => {
        if (cycle.value === null || deleting.value) {
            return false;
        }

        const checkupId = cycle.value.checkup_id;
        deleting.value = true;

        try {
            await medicationCheckupApi().deleteCheckup(checkupId);
            reset();

            return true;
        } catch (exception) {
            if (statusOf(exception) === 409) {
                useToastStore().showError({content: "Ce bilan ne peut pas être supprimé."});

                await refreshCycle();

                return false;
            }

            useToastStore().showError({content: "Le bilan n'a pas pu être supprimé."});

            return false;
        } finally {
            deleting.value = false;
        }
    };

    const reset = (): void => {
        cycle.value = null;
        customerId.value = null;
        displayedStepKey.value = null;
        stepLoading.value = false;
        deleting.value = false;
        error.value = null;
        loading.value = false;
    };

    return {
        cycle,
        customerId,
        loading,
        error,
        stepLoading,
        deleting,
        displayedStepKey,
        steps,
        currentStep,
        displayedStep,
        displayedBlocks,
        nextStep,
        canOpenStep,
        declaredSteps,
        declaredStep,
        declaredBlock,
        load,
        submitBlock,
        scanBlock,
        analyzeBlock,
        draftBlock,
        refreshCycle,
        openStep,
        deleteCheckup,
        reset
    };
});

//https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMedicationCheckupStore, import.meta.hot));
}
